import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Tag } from 'lucide-react';
import { blogArticles } from '../data/blogArticles';
import { BlogArticleSEO } from './SEO';
import { Banner } from './Banner';


export const BlogArticle = () => {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen pt-8 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="heading-1 mb-6">Article Not Found</h1>
          <p className="body-large mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn-primary">
            <ArrowLeft size={18} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // SEO for article
  const seoArticle = {
    ...article,
    metaTitle: article.metaTitle || article.title,
    metaDescription: article.metaDescription || article.excerpt
  };

  // Programmatic SEO fallback: ensures head tags are present if Helmet doesn't inject them
  useEffect(() => {
    if (!article) return;

    const head = document.head;
    const currentOrigin = window.location.origin || '';

    const setOrCreateMeta = (selector, attrs) => {
      let el = head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') el.setAttribute(k, v);
      });
      return el;
    };

    const setOrCreateLinkCanonical = (href) => {
      let link = head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        head.appendChild(link);
      }
      link.setAttribute('href', href);
      return link;
    };

    // Title (Helmet does this too; keep for consistency)
    if (article.title) document.title = article.title;

    // Canonical - using dynamic origin
    setOrCreateLinkCanonical(`${currentOrigin}/blog/${article.slug}`);

    // Prepare full image URL for meta tags
    const fullImageUrl = article.image 
      ? (article.image.startsWith('/api/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${article.image}` 
          : article.image)
      : undefined;

    // OpenGraph
    setOrCreateMeta('meta[property="og:title"]', { property: 'og:title', content: article.metaTitle || article.title });
    setOrCreateMeta('meta[property="og:description"]', { property: 'og:description', content: article.metaDescription || article.excerpt });
    if (fullImageUrl) setOrCreateMeta('meta[property="og:image"]', { property: 'og:image', content: fullImageUrl });
    setOrCreateMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url', content: `${currentOrigin}/blog/${article.slug}` });

    // Twitter (optional but helpful)
    setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: article.metaTitle || article.title });
    setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: article.metaDescription || article.excerpt });
    if (fullImageUrl) setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: fullImageUrl });

    // JSON-LD Article - using dynamic origin
    const existingJsonLd = head.querySelector('script[data-source="blog-article-jsonld"]');
    if (existingJsonLd) existingJsonLd.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-source', 'blog-article-jsonld');
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: fullImageUrl ? [fullImageUrl] : undefined,
      author: {
        '@type': 'Person',
        name: article.author
      },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${currentOrigin}/blog/${article.slug}`
      }
    });
    head.appendChild(script);

    // Cleanup function: optional (we keep tags for correctness after navigation)
    return () => {};
  }, [article]);

  return (
    <>
      <BlogArticleSEO article={seoArticle} />
      {/* Shared Banner + Pill (reduced) */}
      <Banner pillText={article.category || 'Article'} reduced={true} />
      <section className="pad-120">
      <div className="container mx-auto px-4">
        <div className="container mx-auto px-4">
          {/* Back to Blog Link */}
          <div className="mb-8">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-gray-400 hover:text-primary transition-colors"
              style={{ color: 'var(--brand-primary)' }}
            >
            <ArrowLeft size={18} className="mr-2" />
            Back to Blog
          </Link>
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto">
          <header className="mt-8 mb-8">
            {/* Category Badge */}
            <span className="inline-block px-4 py-2 text-xs font-semibold uppercase tracking-wide rounded-full mb-6" style={{ backgroundColor: 'rgba(232, 184, 109, 0.2)', color: 'var(--brand-primary)' }}>
              {article.category}
            </span>

            {/* Title */}
            <h1 className="heading-1 mb-6">
              {article.title}
            </h1>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-gray-400">
              <div className="flex items-center">
                <User size={16} className="mr-2" />
                <span>{article.author}</span>
              </div>
              <div className="flex items-center">
                <Calendar size={16} className="mr-2" />
                <span>{new Date(article.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center">
                <Clock size={16} className="mr-2" />
                <span>{article.readTime}</span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="aspect-video mb-8 rounded-2xl overflow-hidden">
              <img 
                src={article.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${article.image}` : article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full">
                    <Tag size={12} className="mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Article Content */}
          <div 
            className="prose prose-lg max-w-none blog-content"
            style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.8'
            }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <footer className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: 'var(--brand-primary)' }}>
                  <span className="text-black font-bold text-xl">DM</span>
                </div>
                <div>
                  <div className="font-semibold" style={{ color: 'var(--brand-primary)' }}>
                    Douglas Mitchell, PT, DPT
                  </div>
                  <div className="text-gray-400 text-sm">
                    Arizona-born Physical Therapist • 20+ Years Experience
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?focus=contact" className="btn-primary">
                  Schedule Consultation
                </Link>
                <Link to="/blog" className="btn-secondary">
                  More Articles
                </Link>
              </div>
            </div>

            {/* Prominent CTA for Vertigo article */}
            {article.slug === 'vertigo-and-dizziness-care' && (
              <div className="mt-8 p-6 rounded-2xl text-center" style={{ background: 'linear-gradient(to right, rgba(232,184,109,0.15), rgba(232,184,109,0.08))', border: '1px solid rgba(232,184,109,0.25)' }}>
                <h3 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Ready to Feel Steady Again?</h3>
                <p className="body-medium text-gray-300 mb-6 max-w-2xl mx-auto">Book a focused vestibular evaluation for BPPV, dizziness, and imbalance. Quick relief is possible with the right plan.</p>
                <Link to="/contact" className="btn-primary text-lg px-10 py-4">
                  Book Vertigo Evaluation
                </Link>
              </div>
            )}
          </footer>
        </article>

        {/* Related Articles CTA */}
        <div className="mt-16 p-8 rounded-2xl text-center max-w-2xl mx-auto" style={{ background: 'linear-gradient(to right, rgba(232, 184, 109, 0.1), rgba(232, 184, 109, 0.05))', border: '1px solid rgba(232, 184, 109, 0.2)' }}>
          <h3 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Need Physical Therapy?</h3>
          <p className="body-medium mb-6">
            If you're dealing with any of the conditions discussed in this article, 
            I'm here to help you maximize your body's healing potential.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?focus=contact" className="btn-primary">
              Schedule Consultation
            </Link>
            <Link to="/services" className="btn-secondary">
              View Services
            </Link>
          </div>
        </div>
        </div>
        </div>
      </section>
    </>
  );
};